import React, { useEffect, useState } from "react";
import { getStudents } from "../../../services/admin.services";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Download } from "lucide-react";

function ShortlistingTable() {
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage, setStudentsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();

  // Fetch students data
  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const response = await getStudents();
        setStudents(response.data.data || []);
        setTotalCount(response.data.data?.length || 0);
      } catch (error) {
        console.error("Error fetching students:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  // Navigate to detail page
  const handleRowClick = (studentId) => {
    navigate(`/admin/application/shortlisting/student/${studentId}`);
  };

  // Sort function
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Export to Excel
  const handleDownloadExcel = () => {
    const formattedData = filteredStudents.map((student, index) => ({
      SrNo: index + 1,
      StudentName: student.username,
      Email: student.email,
      MobileNumber: student.phone_number || "-",
      Location: student.city || "-",
      Status: student.status || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Set column widths
    const widths = [
      { wch: 5 }, // Sr No
      { wch: 20 }, // Student Name
      { wch: 30 }, // Email
      { wch: 15 }, // Mobile Number
      { wch: 15 }, // Location
      { wch: 15 }, // Status
    ];

    worksheet["!cols"] = widths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");

    XLSX.writeFile(workbook, "shortlisted_students.xlsx");
  };

  // Filter and sort students
  const filteredStudents = React.useMemo(() => {
    let result = Array.isArray(students)
      ? students.filter(
          (student) =>
            student.username
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (student.city &&
              student.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (student.status &&
              student.status
                .toLowerCase()
                .includes(searchTerm.toLowerCase())) ||
            (student.phone_number && student.phone_number.includes(searchTerm))
        )
      : [];

    if (sortConfig.key) {
      result.sort((a, b) => {
        // Handle null values
        if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;
        if (!a[sortConfig.key])
          return sortConfig.direction === "ascending" ? -1 : 1;
        if (!b[sortConfig.key])
          return sortConfig.direction === "ascending" ? 1 : -1;

        // For normal properties
        if (a[sortConfig.key].toLowerCase() < b[sortConfig.key].toLowerCase()) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key].toLowerCase() > b[sortConfig.key].toLowerCase()) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [students, searchTerm, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  );

  return (
    <div className="w-full">
      {/* Header with search and export */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          Shortlisting
          {totalCount > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({filteredStudents.length} students)
            </span>
          )}
        </h2>

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {/* Search box */}
          <div className="relative w-full sm:w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by Student Name"
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
            />
          </div>

          {/* Export button */}
          <button
            onClick={handleDownloadExcel}
            className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            disabled={filteredStudents.length === 0}
          >
            <Download size={20} />
            <span>Export Excel</span>
          </button>
        </div>
      </div>

      {/* Records per page selector */}
      <div className="mb-4 flex justify-end">
        <label className="flex items-center text-sm text-gray-600">
          <span className="mr-2">Show</span>
          <select
            value={studentsPerPage}
            onChange={(e) => {
              setStudentsPerPage(Number(e.target.value));
              setCurrentPage(1); // Reset to first page when changing page size
            }}
            className="border border-gray-300 rounded px-2 py-1"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span className="ml-2">entries</span>
        </label>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-gray-100 text-gray-600 text-sm leading-normal">
              <th className="py-3 px-6 text-left">Sr No</th>
              <th
                className="py-3 px-6 text-left cursor-pointer"
                onClick={() => requestSort("username")}
              >
                Student Name{" "}
                {sortConfig.key === "username" && (
                  <span>
                    {sortConfig.direction === "ascending" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="py-3 px-6 text-left cursor-pointer"
                onClick={() => requestSort("email")}
              >
                Email ID{" "}
                {sortConfig.key === "email" && (
                  <span>
                    {sortConfig.direction === "ascending" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th className="py-3 px-6 text-left hidden sm:table-cell">
                Mobile Number
              </th>
              <th
                className="py-3 px-6 text-left hidden md:table-cell cursor-pointer"
                onClick={() => requestSort("city")}
              >
                Location{" "}
                {sortConfig.key === "city" && (
                  <span>
                    {sortConfig.direction === "ascending" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="py-3 px-6 text-left cursor-pointer"
                onClick={() => requestSort("status")}
              >
                Status{" "}
                {sortConfig.key === "status" && (
                  <span>
                    {sortConfig.direction === "ascending" ? "↑" : "↓"}
                  </span>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="py-4 px-6 text-center">
                  <div className="flex justify-center items-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500"></div>
                    <span className="text-base">Loading students...</span>
                  </div>
                </td>
              </tr>
            ) : currentStudents.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-4 px-6 text-center text-gray-500">
                  {searchTerm ? (
                    <div>
                      <p className="font-medium text-base">
                        No matching records found
                      </p>
                      <p className="text-base mt-2">
                        Try adjusting your search criteria
                      </p>
                    </div>
                  ) : (
                    <p className="font-medium text-base">
                      No students available
                    </p>
                  )}
                </td>
              </tr>
            ) : (
              currentStudents.map((student, index) => (
                <tr
                  key={student.student_id || index}
                  className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowClick(student.student_id)}
                >
                  <td className="py-3 px-6 text-base">
                    {indexOfFirstStudent + index + 1}
                  </td>
                  <td className="py-3 px-6 text-base font-medium">
                    {student.username}
                  </td>
                  <td className="py-3 px-6 text-base">{student.email}</td>
                  <td className="py-3 px-6 text-base hidden sm:table-cell">
                    {student.phone_number || "—"}
                  </td>
                  <td className="py-3 px-6 text-base hidden md:table-cell">
                    {student.city || "—"}
                  </td>
                  <td className="py-3 px-6">
                    <span
                      className={`px-3 py-1 inline-flex text-sm font-medium rounded-full ${
                        student.status === "complete"
                          ? "bg-green-100 text-green-800"
                          : student.status === "document_pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : student.status?.includes("university")
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {student.status || "—"}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      {!loading && totalPages > 0 && (
        <div className="mt-4 flex flex-col sm:flex-row justify-between items-center">
          <div className="text-sm text-gray-600 mb-3 sm:mb-0">
            Showing {indexOfFirstStudent + 1} to{" "}
            {Math.min(indexOfLastStudent, filteredStudents.length)} of{" "}
            {filteredStudents.length} entries
          </div>

          <div className="flex">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="mx-1 px-3 py-1 border rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {Array.from(
              { length: Math.min(totalPages, 5) },
              (_, i) => i + 1
            ).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`mx-1 w-8 h-8 flex items-center justify-center border rounded ${
                  currentPage === page
                    ? "bg-gray-200 border-gray-300"
                    : "bg-white border-gray-300 hover:bg-gray-100"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || totalPages === 0}
              className="mx-1 px-3 py-1 border rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShortlistingTable;
